"use client"
import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import NavBar from "../components/Nav"
import { supabase } from "../lib/supabaseClient"
import TeamPhoto from "../assets/Team Photo.jpg"
import TeamSecondPhoto from "../assets/Team @.jpg"
import Victor from "../assets/Victor1.jpg"
import Team3rdPhoto from "../assets/Team3rdPhoto.jpg"

const ImageUploadForm = () => {
  // State variables
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [previewImage, setPreviewImage] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [formErrors, setFormErrors] = useState({})
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "team",
    alt: "",
  })

  // CHANGE Add new state for image management
  const [allImages, setAllImages] = useState([])
  const [isLoadingImages, setIsLoadingImages] = useState(true)
  const [editingImage, setEditingImage] = useState(null)
  const [editFormData, setEditFormData] = useState({
    title: "",
    description: "",
    category: "",
    alt_text: "",
  })

  // State for delete confirmation modal
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    imageId: null,
    storagePath: null,
  })

  // Refs
  const fileInputRef = useRef(null)
  const dropZoneRef = useRef(null)

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  }

  const pulseAnimation = {
    initial: { scale: 1 },
    animate: {
      scale: [1, 1.03, 1],
      boxShadow: [
        "0px 0px 0px rgba(250, 204, 21, 0)",
        "0px 0px 15px rgba(250, 204, 21, 0.5)",
        "0px 0px 0px rgba(250, 204, 21, 0)",
      ],
      transition: {
        duration: 2,
        repeat: Number.POSITIVE_INFINITY,
        repeatType: "loop",
      },
    },
  }

  // Sample images for quick selection
  const sampleImages = [
    { src: TeamPhoto, name: "Team Photo" },
    { src: TeamSecondPhoto, name: "Team Second Photo" },
    { src: Victor, name: "Dr. Victor" },
    { src: Team3rdPhoto, name: "Team Third Photo" },
  ]

  // Available categories
  const categories = [
    { id: "team", name: "Team Photos" },
    { id: "worship", name: "Worship Events" },
    { id: "concerts", name: "Concerts" },
    { id: "behind", name: "Behind the Scenes" },
  ]

  // Handle drag events for the drop zone
  useEffect(() => {
    const dropZone = dropZoneRef.current
    if (!dropZone) return

    const handleDragOver = (e) => {
      e.preventDefault()
      e.stopPropagation()
      dropZone.classList.add("border-yellow-400")
      dropZone.classList.remove("border-indigo-600")
    }

    const handleDragLeave = (e) => {
      e.preventDefault()
      e.stopPropagation()
      dropZone.classList.remove("border-yellow-400")
      dropZone.classList.add("border-indigo-600")
    }

    const handleDrop = (e) => {
      e.preventDefault()
      e.stopPropagation()
      dropZone.classList.remove("border-yellow-400")
      dropZone.classList.add("border-indigo-600")

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelection(e.dataTransfer.files[0])
      }
    }

    dropZone.addEventListener("dragover", handleDragOver)
    dropZone.addEventListener("dragleave", handleDragLeave)
    dropZone.addEventListener("drop", handleDrop)

    return () => {
      dropZone.removeEventListener("dragover", handleDragOver)
      dropZone.removeEventListener("dragleave", handleDragLeave)
      dropZone.removeEventListener("drop", handleDrop)
    }
  }, [])

  // Reset success message after 5 seconds
  useEffect(() => {
    let timer
    if (showSuccessMessage) {
      timer = setTimeout(() => {
        setShowSuccessMessage(false)
      }, 5000)
    }
    return () => clearTimeout(timer)
  }, [showSuccessMessage])

  // CHANGE Add useEffect to fetch all images from database
  useEffect(() => {
    fetchAllImages()
  }, [])

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null,
      })
    }
  }

  // Handle file selection (from input or drop)
  const handleFileSelection = (file) => {
    if (!file) return

    // Validate file type
    if (!file.type.includes("image/")) {
      setFormErrors({
        ...formErrors,
        file: "Please select an image file (JPG, PNG, GIF, etc.)",
      })
      return
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setFormErrors({
        ...formErrors,
        file: "File size exceeds 5MB limit. Please select a smaller image.",
      })
      return
    }

    // Clear any previous file errors
    if (formErrors.file) {
      setFormErrors({
        ...formErrors,
        file: null,
      })
    }

    setSelectedFile(file)

    // Create preview URL
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreviewImage(reader.result)
    }
    reader.readAsDataURL(file)
  }

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0]
    handleFileSelection(file)
  }

  // Handle sample image selection
  const handleSampleImageSelect = (imageSrc) => {
    setPreviewImage(imageSrc)
    setSelectedFile(null)

    // Clear file error if it exists
    if (formErrors.file) {
      setFormErrors({
        ...formErrors,
        file: null,
      })
    }
  }

  // Validate form before submission
  const validateForm = () => {
    const errors = {}

    if (!formData.title.trim()) errors.title = "Title is required"
    if (!formData.description.trim()) errors.description = "Description is required"
    if (!formData.category) errors.category = "Category is required"
    if (!previewImage && !selectedFile) errors.file = "Please select or upload an image"

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Simulate upload progress
  const simulateProgress = () => {
    let progress = 0
    const interval = setInterval(() => {
      progress += Math.random() * 15
      if (progress > 100) {
        progress = 100
        clearInterval(interval)
      }
      setUploadProgress(Math.floor(progress))
    }, 300)

    return interval
  }

  // CHANGE Add function to fetch all images
  const fetchAllImages = async () => {
    try {
      setIsLoadingImages(true)
      const { data, error } = await supabase.from("images").select("*").order("created_at", { ascending: false })

      if (error) {
        console.error("Error fetching images:", error)
        return
      }

      setAllImages(data || [])
    } catch (err) {
      console.error("Error in fetchAllImages:", err)
    } finally {
      setIsLoadingImages(false)
    }
  }

  // CHANGE Improve handleEditClick to ensure category ID is properly loaded
  const handleEditClick = (image) => {
    console.log("[v0] Editing image:", image)
    setEditingImage(image.id)
    setEditFormData({
      title: image.title || "",
      description: image.description || "",
      category: image.category || "team",
      alt_text: image.alt_text || "",
    })
  }

  // CHANGE Fix handleSaveEdit to properly validate and log errors
  const handleSaveEdit = async (imageId) => {
    try {
      console.log("[v0] Attempting to save edit for image:", imageId, "with data:", editFormData)

      // CHANGE Validate that required fields are not empty
      if (!editFormData.title || !editFormData.description || !editFormData.category) {
        setFormErrors({ submit: "Title, description, and category are required" })
        return
      }

      const updateData = {
        title: editFormData.title,
        description: editFormData.description,
        category: editFormData.category,
        alt_text: editFormData.alt_text,
      }

      const { data, error } = await supabase.from("images").update(updateData).eq("id", imageId).select()

      console.log("[v0] Update response:", data, error)

      if (error) {
        console.error("[v0] Error updating image:", error)
        setFormErrors({ submit: `Failed to update image: ${error.message}` })
        return
      }

      console.log("[v0] Successfully updated image, refreshing list...")
      setEditingImage(null)
      setFormErrors({ submit: null })
      await fetchAllImages()
    } catch (err) {
      console.error("[v0] Error in handleSaveEdit:", err)
      setFormErrors({ submit: "Failed to update image: " + err.message })
    }
  }

  // CHANGE Add function to delete image
  const handleDeleteClick = (imageId, storagePath) => {
    setDeleteConfirmation({
      isOpen: true,
      imageId,
      storagePath,
    })
  }

  const handleConfirmDelete = async () => {
    const { imageId, storagePath } = deleteConfirmation

    try {
      // Delete from storage if it's a custom uploaded image
      if (storagePath && !storagePath.includes("assets/sample")) {
        try {
          const fileName = storagePath.split("/").pop()
          await supabase.storage.from("images").remove([fileName])
        } catch (storageErr) {
          console.error("Error deleting from storage:", storageErr)
        }
      }

      // Delete from database
      const { error } = await supabase.from("images").delete().eq("id", imageId)

      if (error) {
        console.error("Error deleting image:", error)
        setFormErrors({ submit: "Failed to delete image" })
        setDeleteConfirmation({ isOpen: false, imageId: null, storagePath: null })
        return
      }

      setDeleteConfirmation({ isOpen: false, imageId: null, storagePath: null })
      fetchAllImages()
    } catch (err) {
      console.error("Error in handleDeleteImage:", err)
      setFormErrors({ submit: "Failed to delete image" })
      setDeleteConfirmation({ isOpen: false, imageId: null, storagePath: null })
    }
  }

  const handleCancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, imageId: null, storagePath: null })
  }

  // Replace the handleSubmit function with this improved version
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)
    setUploadProgress(0)
    setFormErrors({})

    const progressInterval = simulateProgress()

    try {
      let imageUrl = ""
      // CHANGE Declare fileName outside the conditional to avoid undefined variable
      let fileName = ""

      // If using a sample image (which is already in the assets folder)
      if (previewImage && !selectedFile) {
        // For sample images, we can use the path directly since they're already in the assets
        imageUrl = previewImage
        console.log("Using sample image:", imageUrl)
      }
      // If user uploaded a custom image
      else if (selectedFile) {
        console.log("Uploading custom image:", selectedFile.name, selectedFile.type, selectedFile.size)

        // Create a unique filename to avoid collisions
        const fileExt = selectedFile.name.split(".").pop()
        fileName = `gallery_${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`
        const filePath = `${fileName}`

        console.log("Uploading to path:", filePath)

        // Upload image to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from("images")
          .upload(filePath, selectedFile, {
            cacheControl: "3600",
            upsert: false,
          })

        console.log("Upload response:", uploadData, uploadError)

        if (uploadError) {
          console.error("Supabase upload error:", uploadError)
          throw new Error(`Upload failed: ${uploadError.message || "Unknown error"}`)
        }

        // Get public URL for the uploaded image
        const {
          data: { publicUrl },
        } = supabase.storage.from("images").getPublicUrl(filePath)

        console.log("Public URL:", publicUrl)
        imageUrl = publicUrl
      } else {
        throw new Error("Please select or upload an image")
      }

      // Insert image data into Supabase table
      console.log("Inserting image with URL:", imageUrl)
      const { data, error } = await supabase.from("images").insert([
        {
          title: formData.title,
          description: formData.description,
          category: formData.category,
          alt_text: formData.alt || formData.title,
          image_url: imageUrl,
          storage_path: selectedFile ? `images/${fileName}` : "assets/sample",
        },
      ])

      if (error) {
        console.error("Supabase insert error:", error)
        throw new Error(`Database error: ${error.message || "Failed to save image"}`)
      }

      // Clear the form
      setFormData({
        title: "",
        description: "",
        category: "team",
        alt: "",
      })
      setPreviewImage(null)
      setSelectedFile(null)

      // Show success message
      setShowSuccessMessage(true)

      // Redirect to gallery after a short delay
      setTimeout(() => {
        window.location.href = "/gallery"
      }, 3000)
    } catch (error) {
      console.error("Error uploading image:", error)
      setFormErrors({
        ...formErrors,
        submit: error.message || "Failed to upload image. Please try again.",
      })
    } finally {
      clearInterval(progressInterval)
      setUploadProgress(100)
      setTimeout(() => {
        setIsSubmitting(false)
        setUploadProgress(0)
      }, 500)
    }
  }

  // Handle cancel button click
  const handleCancel = () => {
    window.location.href = "/gallery"
  }

  return (
    <>
      <NavBar />

      <div className="min-h-screen bg-gradient-to-b from-indigo-950 via-indigo-900 to-indigo-950 text-white py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          {/* Changed max-w from 4xl to 6xl */}
          {/* Upload Section */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Upload New Image</h1>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-500 to-yellow-300 mx-auto mb-6"></div>
            <p className="text-lg text-indigo-200 max-w-2xl mx-auto">
              Share your beautiful moments with the Exodus Music Ministry community
            </p>
          </motion.div>
          {/* Success Message */}
          <AnimatePresence>
            {showSuccessMessage && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-green-500/20 border border-green-500 text-green-200 px-6 py-4 rounded-lg mb-8 text-center"
              >
                <div className="flex items-center justify-center">
                  <svg
                    className="w-6 h-6 mr-2 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="font-medium">Image uploaded successfully!</span>
                </div>
                <p className="mt-1 text-sm">Your image has been added to the gallery.</p>
              </motion.div>
            )}
          </AnimatePresence>
          {/* Main Form */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="bg-indigo-900/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-indigo-800 mb-12"
          >
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column - Form Fields */}
                <div className="space-y-6">
                  {/* Title */}
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-indigo-200 mb-2">
                      Image Title <span className="text-yellow-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-indigo-800/50 border ${
                        formErrors.title ? "border-red-500" : "border-indigo-700"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 text-white`}
                      placeholder="Enter a title for this image"
                    />
                    {formErrors.title && <p className="mt-1 text-sm text-red-400">{formErrors.title}</p>}
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-indigo-200 mb-2">
                      Description <span className="text-yellow-400">*</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows="4"
                      className={`w-full px-4 py-3 bg-indigo-800/50 border ${
                        formErrors.description ? "border-red-500" : "border-indigo-700"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 text-white`}
                      placeholder="Describe this image..."
                    ></textarea>
                    {formErrors.description && <p className="mt-1 text-sm text-red-400">{formErrors.description}</p>}
                  </div>

                  {/* Category */}
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-indigo-200 mb-2">
                      Category <span className="text-yellow-400">*</span>
                    </label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-indigo-800/50 border border-indigo-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 text-white"
                    >
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Alt Text */}
                  <div>
                    <label htmlFor="alt" className="block text-sm font-medium text-indigo-200 mb-2">
                      Alt Text <span className="text-indigo-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      id="alt"
                      name="alt"
                      value={formData.alt}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-indigo-800/50 border border-indigo-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 text-white"
                      placeholder="Alternative text for accessibility"
                    />
                    <p className="mt-1 text-xs text-indigo-400">
                      Describe the image for screen readers and accessibility
                    </p>
                  </div>
                </div>

                {/* Right Column - Image Upload */}
                <div className="space-y-6">
                  {/* Image Preview */}
                  {previewImage && (
                    <div className="mb-4">
                      <div className="relative w-full h-48 rounded-lg overflow-hidden mb-2">
                        <img
                          src={previewImage || "/placeholder.svg"}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setPreviewImage(null)
                            setSelectedFile(null)
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            ></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Drop Zone */}
                  {!previewImage && (
                    <div
                      ref={dropZoneRef}
                      className="border-2 border-dashed border-indigo-600 rounded-lg p-8 text-center cursor-pointer transition-colors duration-300 hover:border-yellow-400"
                      onClick={() => fileInputRef.current.click()}
                    >
                      <svg
                        className="w-12 h-12 text-indigo-400 mx-auto mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M4 16l4.586-4.586A2 2 0 012.828 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        ></path>
                      </svg>
                      <p className="text-indigo-300 mb-2">Drag and drop your image here, or click to browse</p>
                      <p className="text-indigo-400 text-sm">Maximum file size: 5MB</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                      <button
                        type="button"
                        className="mt-4 px-4 py-2 bg-indigo-700 hover:bg-indigo-600 rounded-lg text-white text-sm transition-colors"
                      >
                        Select File
                      </button>
                    </div>
                  )}

                  {/* File Error Message */}
                  {formErrors.file && <p className="text-sm text-red-400 text-center">{formErrors.file}</p>}

                  {/* Sample Images */}
                  <div>
                    <p className="text-sm text-indigo-300 mb-2">Or select from sample images:</p>
                    <div className="grid grid-cols-2 gap-3">
                      {sampleImages.map((image, index) => (
                        <div
                          key={index}
                          onClick={() => handleSampleImageSelect(image.src)}
                          className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                            previewImage === image.src
                              ? "border-yellow-400 ring-2 ring-yellow-400/50"
                              : "border-transparent hover:border-indigo-600"
                          }`}
                        >
                          <div className="aspect-square">
                            <img
                              src={image.src || "/placeholder.svg"}
                              alt={image.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="p-1 text-center text-xs bg-indigo-800/80 text-indigo-200 truncate">
                            {image.name}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Error */}
              {formErrors.submit && (
                <div className="mt-6 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-200 text-center">
                  {formErrors.submit}
                </div>
              )}

              {/* Upload Progress */}
              {isSubmitting && uploadProgress > 0 && (
                <div className="mt-6">
                  <div className="flex justify-between text-sm text-indigo-300 mb-1">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-indigo-800 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-yellow-500 to-yellow-300 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-end mt-8">
                <motion.button
                  type="button"
                  onClick={handleCancel}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 py-3 bg-indigo-800 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </motion.button>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  variants={pulseAnimation}
                  initial="initial"
                  animate={isSubmitting ? "initial" : "animate"}
                  whileHover={isSubmitting ? {} : { scale: 1.02 }}
                  whileTap={isSubmitting ? {} : { scale: 0.98 }}
                  className={`px-8 py-3 bg-gradient-to-r from-yellow-500 to-yellow-400 text-indigo-950 font-bold rounded-lg flex items-center justify-center ${
                    isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-5 w-5 text-indigo-950"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        ></path>
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    <>
                      Upload to Gallery
                      <svg
                        className="ml-2 w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        ></path>
                      </svg>
                    </>
                  )}
                </motion.button>
              </div>
            </form>
          </motion.div>
          {/* CHANGE Add Image Management Section */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="bg-indigo-900/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-indigo-800"
          >
            <h2 className="text-3xl font-bold mb-2 text-yellow-400">Manage Gallery Images</h2>
            <div className="w-16 h-1 bg-gradient-to-r from-yellow-500 to-yellow-300 mb-6"></div>

            {/* Loading State */}
            {isLoadingImages && (
              <div className="flex justify-center items-center py-12">
                <svg
                  className="animate-spin h-8 w-8 text-yellow-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span className="ml-3 text-indigo-200">Loading images...</span>
              </div>
            )}

            {/* No Images State */}
            {!isLoadingImages && allImages.length === 0 && (
              <div className="text-center py-12">
                <svg
                  className="w-16 h-16 text-indigo-400 mx-auto mb-4 opacity-50"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  ></path>
                </svg>
                <p className="text-indigo-300 text-lg">No images in gallery yet.</p>
              </div>
            )}

            {/* Images Grid */}
            {!isLoadingImages && allImages.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence>
                  {allImages.map((image) => (
                    <motion.div
                      key={image.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="bg-indigo-800/50 rounded-lg overflow-hidden border border-indigo-700 hover:border-yellow-400 transition-all"
                    >
                      {/* Image Display */}
                      <div className="relative w-full h-48 overflow-hidden">
                        <img
                          src={image.image_url || "/placeholder.svg"}
                          alt={image.alt_text}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-2 right-2 bg-indigo-600/80 px-2 py-1 rounded text-xs text-yellow-300 font-semibold">
                          {categories.find((c) => c.id === image.category)?.name || "Unknown"}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-4 space-y-3">
                        {editingImage === image.id ? (
                          // Edit Mode
                          <div className="space-y-3">
                            <input
                              type="text"
                              value={editFormData.title}
                              onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                              placeholder="Title"
                              className="w-full px-3 py-2 bg-indigo-700 border border-indigo-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                            />
                            <textarea
                              value={editFormData.description}
                              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                              placeholder="Description"
                              rows="3"
                              className="w-full px-3 py-2 bg-indigo-700 border border-indigo-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                            />
                            <select
                              value={editFormData.category}
                              onChange={(e) => {
                                console.log("[v0] Category changed to:", e.target.value)
                                setEditFormData({ ...editFormData, category: e.target.value })
                              }}
                              className="w-full px-3 py-2 bg-indigo-700 border border-indigo-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                            >
                              <option value="">Select a category</option>
                              {categories.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                            <input
                              type="text"
                              value={editFormData.alt_text}
                              onChange={(e) => setEditFormData({ ...editFormData, alt_text: e.target.value })}
                              placeholder="Alt Text"
                              className="w-full px-3 py-2 bg-indigo-700 border border-indigo-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                            />
                            <div className="flex gap-2 pt-2">
                              <button
                                onClick={() => {
                                  console.log("[v0] Save button clicked for image:", image.id)
                                  handleSaveEdit(image.id)
                                }}
                                className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 rounded text-white text-sm font-medium transition-colors"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  console.log("[v0] Cancel edit for image:", image.id)
                                  setEditingImage(null)
                                }}
                                className="flex-1 px-3 py-2 bg-indigo-700 hover:bg-indigo-600 rounded text-white text-sm font-medium transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <>
                            <div>
                              <h3 className="font-bold text-white text-sm line-clamp-2">{image.title}</h3>
                              <p className="text-indigo-300 text-xs mt-1 line-clamp-2">{image.description}</p>
                            </div>
                            {image.alt_text && (
                              <div className="bg-indigo-700/50 p-2 rounded text-xs text-indigo-200 line-clamp-1">
                                <span className="font-semibold">Alt:</span> {image.alt_text}
                              </div>
                            )}
                            <div className="flex gap-2 pt-2">
                              <button
                                onClick={() => handleDeleteClick(image.id, image.storage_path)}
                                className="w-full px-3 py-2 bg-red-600 hover:bg-red-700 rounded text-white text-sm font-medium transition-colors flex items-center justify-center gap-1"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  ></path>
                                </svg>
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </motion.div>
          {/* Tips Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-12 bg-indigo-900/30 backdrop-blur-sm rounded-xl p-6 border border-indigo-800/50"
          >
            <h3 className="text-xl font-semibold text-yellow-400 mb-4">Tips for Great Gallery Images</h3>
            <ul className="space-y-2 text-indigo-200">
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-yellow-400 mr-2 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <span>Choose high-quality images that are clear and well-lit</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-yellow-400 mr-2 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <span>Add descriptive titles and detailed descriptions to help others understand the context</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-yellow-400 mr-2 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <span>Select the appropriate category to help organize the gallery</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-yellow-400 mr-2 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <span>Include alt text to make your images accessible to everyone</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex items-center justify-center bg-black/50 z-50"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.9 }}
            className="bg-indigo-950 rounded-lg p-8 shadow-lg text-white"
          >
            <h3 className="text-xl font-bold mb-4">Confirm Delete</h3>
            <p className="text-lg mb-6">Are you sure you want to delete this image?</p>
            <div className="flex gap-4 justify-end">
              <button
                onClick={handleCancelDelete}
                className="flex-1 px-4 py-2 bg-indigo-700 hover:bg-indigo-600 text-white font-medium rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded transition-colors"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  )
}

export default ImageUploadForm
